import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Assistant from '../models/Assistant';
import { ASSISTANT_NAME, READ_ALOUD_STORAGE_KEY } from '../constants/config';
import VoicePlayer from '../models/VoicePlayer';
import { preloadAssistantVoice } from '../utils/speechUtils';
import { readStoredBoolean, writeStoredBoolean } from '../utils/storageUtils';

const TALKING_ANIMATION_MS = 2500;

/**
 * UI state for Fizzy: the current speech bubble, whether Fizzy is talking, and the
 * "Read to me" switch. With the switch on (the default), every new message is read aloud.
 */
export default function useAssistant() {
  const assistant = useMemo(() => new Assistant(ASSISTANT_NAME), []);
  const voicePlayer = useMemo(() => new VoicePlayer(), []);
  const [currentMessage, setCurrentMessage] = useState(() => assistant.createGreeting());
  const [isShowingNewMessage, setIsShowingNewMessage] = useState(false);
  const [isReadingAloud, setIsReadingAloud] = useState(false);
  const [isReadAloudOn, setIsReadAloudOn] = useState(
    () => VoicePlayer.isAvailable() && readStoredBoolean(READ_ALOUD_STORAGE_KEY, true)
  );
  const readingIdRef = useRef(0);
  const isReadAloudOnRef = useRef(isReadAloudOn);

  useEffect(() => {
    if (!isShowingNewMessage) return undefined;
    const timer = setTimeout(() => setIsShowingNewMessage(false), TALKING_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isShowingNewMessage, currentMessage]);

  useEffect(() => {
    preloadAssistantVoice();
    if (typeof fetch === 'function') voicePlayer.loadManifest();
    return () => voicePlayer.stop();
  }, [voicePlayer]);

  const readAloud = useCallback(
    (message) => {
      // Only the latest reading may stop the talking; an older one ending because it was cut off is ignored.
      const readingId = ++readingIdRef.current;
      setIsReadingAloud(true);
      voicePlayer.speak(message.getSpeechSegments(), {
        onEnd: () => {
          if (readingId === readingIdRef.current) setIsReadingAloud(false);
        },
      });
    },
    [voicePlayer]
  );

  const stopReadingAloud = useCallback(() => {
    readingIdRef.current += 1;
    voicePlayer.stop();
    setIsReadingAloud(false);
  }, [voicePlayer]);

  const showMessage = useCallback(
    (message) => {
      setCurrentMessage(message);
      setIsShowingNewMessage(true);
      if (isReadAloudOnRef.current) readAloud(message);
    },
    [readAloud]
  );

  const toggleReadAloud = useCallback(() => {
    const nextIsOn = !isReadAloudOnRef.current;
    isReadAloudOnRef.current = nextIsOn;
    setIsReadAloudOn(nextIsOn);
    writeStoredBoolean(READ_ALOUD_STORAGE_KEY, nextIsOn);
    if (nextIsOn) {
      readAloud(currentMessage);
    } else {
      stopReadingAloud();
    }
  }, [currentMessage, readAloud, stopReadingAloud]);

  const showNextTip = useCallback(() => showMessage(assistant.createIdleTip()), [assistant, showMessage]);

  return {
    assistant,
    currentMessage,
    isTalking: isShowingNewMessage || isReadingAloud,
    isReadAloudOn,
    canReadAloud: VoicePlayer.isAvailable(),
    showMessage,
    showNextTip,
    toggleReadAloud,
  };
}
